import { CENT, type Money } from '@mybooks/ledger';
import { money } from './format';

// Exchange rates on screen (ADR 0029). A rate is base units per one unit of a currency, sent by
// the API as a decimal string with 10 places. Rates are ratios, not money: the inverse shown beside
// a rate is for reading only and never used in a calculation.

export const RATE_SOURCE: Record<string, string> = { manual: 'Entered here', ecb: 'European Central Bank', xe: 'XE' };

/** What the API accepts: positive, at most 9 whole digits and 10 decimals. */
export function validRate(text: string): boolean {
  const t = text.trim();
  return /^\d{1,9}(\.\d{1,10})?$/.test(t) && /[1-9]/.test(t);
}

/** "1.0812000000" -> "1.0812": trailing zeros dropped, at least four decimals kept. */
export function rateText(rate: string): string {
  const [whole = '0', frac = ''] = rate.split('.');
  const trimmed = frac.replace(/0+$/, '');
  return `${whole}.${trimmed.padEnd(4, '0')}`;
}

/** The other direction, for reading: "1.0812" -> "0.924898" (four decimals above 1, else six significant digits). */
export function inverseText(rate: string): string {
  const n = Number(rate);
  if (!(n > 0)) return '';
  const inv = 1 / n;
  return inv >= 1 ? inv.toFixed(4) : inv.toPrecision(6).replace(/0+$/, '');
}

/** "1 EUR = 1.0812 USD · 1 USD = 0.924898 EUR" */
export function rateBothWays(rate: string, currency: string, base: string): string {
  return `1 ${currency} = ${rateText(rate)} ${base} · 1 ${base} = ${inverseText(rate)} ${currency}`;
}

// ---- Amounts in the base currency (ADR 0029) ------------------------------------------------
// A document's or payment's own amounts are in its currency; sums across records (tiles, totals,
// dashboards) must use base amounts. The API sends them for anything posted; a draft has none yet,
// so its base is estimated from its rate, exactly, in bigint.


/** An amount times a rate (base units per one unit), rounded half away from zero to the cent. */
export function mulRate(amount: Money, rate: string): Money {
  const [whole = '0', frac = ''] = rate.trim().split('.');
  const scaled = BigInt(whole + frac.padEnd(10, '0').slice(0, 10)); // rate x 10^10
  const negative = amount < 0n;
  const raw = (negative ? -amount : amount) * scaled; // units x 10^10
  const unit = 10n ** 10n * CENT; // one cent, scaled
  const cents = (raw + unit / 2n) / unit;
  return (negative ? -1n : 1n) * cents * CENT;
}

interface WithBase { exchange_rate?: string | null | undefined }

/** What is still open on a document, in base: base_balance_due once sent, else estimated. */
export function openBase(d: WithBase & { balance_due?: string | null | undefined; base_balance_due?: string | null | undefined }): Money {
  if (d.base_balance_due !== null && d.base_balance_due !== undefined) return money(d.base_balance_due);
  return d.exchange_rate ? mulRate(money(d.balance_due), d.exchange_rate) : money(d.balance_due);
}

/** A document's or order's total in base: base_total once sent, else estimated from its rate. */
export function totalBase(d: WithBase & { total?: string | null | undefined; base_total?: string | null | undefined }): Money {
  if (d.base_total !== null && d.base_total !== undefined) return money(d.base_total);
  return d.exchange_rate ? mulRate(money(d.total), d.exchange_rate) : money(d.total);
}

/** A payment's amount in base. */
export function paymentBase(p: { amount?: string | null | undefined; base_amount?: string | null | undefined }): Money {
  return money(p.base_amount ?? p.amount);
}

/** The currency a record's own amounts are in, falling back to the business's. */
export const currencyOf = (r: { currency?: string | null | undefined } | null | undefined, base: string): string => r?.currency ?? base;

// ---- Revaluation (ADR 0029) -------------------------------------------------------------------

export interface RevaluationLine { kind?: string; difference?: string | null; rate?: string | null; currency?: string }

/** The net unrealised result of revaluation lines: a gain is positive. Assets gain when they rise, liabilities lose. */
export function revaluationResult(lines: readonly RevaluationLine[]): Money {
  return lines.reduce<Money>((s, l) => {
    const d = money(l.difference);
    return l.kind === 'bank' || l.kind === 'receivable' ? s + d : s - d;
  }, 0n);
}

/** Currencies in the lines that have no closing rate yet. */
export const missingRates = (lines: readonly RevaluationLine[]): string[] =>
  [...new Set(lines.filter((l) => !l.rate && l.currency).map((l) => l.currency as string))].sort();
